import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { MessageCircle, Loader2, ExternalLink, Clock, CheckCircle2, XCircle, KeyRound, FileDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PDF_URL = (regId) => `${API}/registrations/${regId}/pdf`;

export default function PaymentPending() {
  const [params] = useSearchParams();
  const regId = params.get("reg");
  const [reg, setReg] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!regId) { setLoading(false); return; }
    try {
      const { data } = await axios.get(`${API}/registrations/${regId}/status`);
      setReg(data);
    } catch { setReg(null); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [regId]);

  // Auto-refresh every 30s while awaiting approval
  useEffect(() => {
    if (!reg || reg.approval_status === "approved" || reg.approval_status === "rejected") return;
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [reg?.approval_status]);

  if (loading) return <div className="p-10 text-center text-[#5A6A94]">Caricamento...</div>;
  if (!regId || !reg) return (
    <div className="page-bg min-h-[70vh] flex items-center justify-center px-4">
      <div className="card-elevated p-8 max-w-md w-full text-center">
        <XCircle size={40} className="mx-auto text-[#F97316]" />
        <p className="mt-3 text-[#5A6A94]">Registrazione non trovata.</p>
        <Link to="/register" className="btn-primary mt-4 inline-flex">Nuova iscrizione</Link>
      </div>
    </div>
  );

  const status = reg.approval_status;
  const isApproved = status === "approved";
  const isRejected = status === "rejected";

  return (
    <div className="py-12 sm:py-16">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-8 fade-up">
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest ${
            isApproved ? "bg-[#E8F8EE] text-[#16A34A]" :
            isRejected ? "bg-[#FFF4EC] text-[#F97316]" :
            "bg-[#FFF7E6] text-[#B45309]"
          }`}>
            {isApproved ? <CheckCircle2 size={14} /> : isRejected ? <XCircle size={14} /> : <Clock size={14} />}
            {isApproved ? "Approvata" : isRejected ? "Non approvata" : "In attesa"}
          </div>
          <h1 className="mt-3 font-display font-extrabold text-3xl sm:text-4xl text-[#0B3D91]" data-testid="pending-title">
            Ciao {reg.nome}!
          </h1>
          <p className="mt-2 text-[#5A6A94]">
            La tua iscrizione (ID: <span className="font-mono text-xs">{regId.slice(0, 8)}</span>) è stata registrata.
          </p>
        </div>

        {isApproved ? (
          <div className="card-elevated p-8 text-center fade-up" data-testid="approved-box">
            <CheckCircle2 size={56} className="mx-auto text-[#16A34A]" />
            <h2 className="mt-3 font-display font-extrabold text-2xl text-[#0B3D91]">Iscrizione approvata!</h2>
            <p className="mt-2 text-[#5A6A94]">
              Crea la tua password per accedere al corso. Abbonamento valido fino al{" "}
              <strong>{reg.subscription_end && new Date(reg.subscription_end).toLocaleDateString("it-IT")}</strong>.
            </p>
            <Link to={`/account/setup?reg=${regId}`} className="btn-primary mt-6 inline-flex" data-testid="create-account-btn">
              <KeyRound size={16} /> Crea il tuo account
            </Link>
            <a
              href={PDF_URL(regId)}
              target="_blank" rel="noopener noreferrer"
              className="btn-outline mt-3 inline-flex ml-2"
              data-testid="download-registration-pdf-btn"
            >
              <FileDown size={16} /> Scarica modulo firmato (PDF)
            </a>
            <p className="mt-3 text-xs text-[#8494B8]">
              Il PDF contiene i tuoi dati, la tua firma e il timbro di approvazione dell'associazione.
            </p>
          </div>
        ) : isRejected ? (
          <div className="card-elevated p-8 text-center fade-up">
            <XCircle size={56} className="mx-auto text-[#F97316]" />
            <h2 className="mt-3 font-display font-extrabold text-2xl text-[#0B3D91]">Iscrizione non approvata</h2>
            {reg.rejection_reason && <p className="mt-2 text-[#5A6A94]"><strong>Motivo:</strong> {reg.rejection_reason}</p>}
            <a href={reg.whatsapp_url} target="_blank" rel="noopener noreferrer" className="btn-primary mt-4 inline-flex">
              <MessageCircle size={16} /> Scrivici su WhatsApp
            </a>
          </div>
        ) : (
          <div className="card-elevated p-6 sm:p-8 fade-up" data-testid="pending-box">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-2xl bg-[#25D366] text-white flex items-center justify-center shadow-lg">
                <MessageCircle size={30} />
              </div>
            </div>
            <h3 className="font-display font-bold text-xl text-[#0B3D91] text-center">Contattaci su WhatsApp per pagare</h3>
            <p className="mt-2 text-sm text-[#5A6A94] text-center">
              Se WhatsApp non si è aperto automaticamente, clicca qui. Ti forniremo i dettagli di pagamento e ti approveremo appena ricevuto.
            </p>
            <a
              href={reg.whatsapp_url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full text-white font-semibold shadow-sm hover:shadow-md transition-all bg-[#25D366] hover:bg-[#1FBB58]"
              data-testid="whatsapp-btn"
            >
              <MessageCircle size={18} /> Apri WhatsApp <ExternalLink size={14} />
            </a>

            <div className="mt-8 pt-6 border-t border-[#E4EAF7] text-center">
              <p className="text-xs text-[#8494B8] mb-3">
                Non serve caricare nulla nell'app. Appena ricevuta la conferma di pagamento su WhatsApp, ti approveremo e potrai creare il tuo account qui.
              </p>
              <button onClick={load} className="btn-outline !py-2 text-sm" data-testid="refresh-status-btn">
                <Clock size={14} /> Aggiorna stato
              </button>
              <p className="mt-4 text-xs text-[#8494B8]">
                📌 <strong>Salva questa pagina nei preferiti</strong> — torna qui per creare l'account dopo l'approvazione.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
